/* rs-gift.js — <rs-gift-panel>, the gift panel (snippets/rs-gift-panel.liquid).
   Two surfaces, never both on one page: the /cart card, and the cart drawer
   (sections/cart-popup.liquid, every page except /cart; Digital #96, Sam
   2026-10-30).

   Writes the gift message to the cart through /cart/update.js:
     note: "GIFT MESSAGE: <text>"
   Free gift wrap was removed (Sam, 2026-10-02). The panel no longer writes the
   "GIFT WRAP: Yes" note line or the "Gift wrap" attribute. LEGACY: a cart saved
   before then may still hold either. WRAP_LINE is kept so such a note is still
   recognised as ours; composeNote() never writes it, so the first save (run on
   load when legacy state is present) drops it. A legacy "Gift wrap" attribute
   is deleted by sending that one key as "" — never the whole attributes
   object. The Cart API merges attribute keys, so any other attribute is
   never overwritten (measured on the dev store 2026-09-28:
   update.js with one key left the other key intact).

   DOES NOT PUBLISH cartUpdate. A publish would hit cart-popup.js's drawer
   auto-open subscriber (it re-opens the drawer within 30s of an add-to-cart)
   and make <cart-items> re-fetch. It only SUBSCRIBES, to clear the gift state
   when the cart empties — Shopify keeps the note and attributes on an empty
   cart (measured), so an abandoned message would otherwise ride the next order.
   assets/rs-gift-guard.js does the same job where no panel renders (gift
   panel setting off, or an empty /cart); it stands down on any page with this
   element, so each page has exactly one clearing writer. The cartUpdate payload
   carries the server note, so the clear works even when this panel was seeded
   before the note changed (drawer panel, Digital #96 build note 6).

   Staying in sync with the other surface: reseed(note) re-reads cart.note into
   the field when the shopper is not editing it. cart-popup.js calls it on every
   drawer open (its /cart.js fetch); this file calls it on a back/forward-cache
   restore (pageshow, persisted).

   Timing: input is debounced; change/blur save at once.
   Saves are serialised and each one sends the state as it is WHEN IT RUNS, so
   the last write always wins. The form="cart" hidden inputs are updated on
   every keystroke, so the checkout button's native POST carries the latest
   text even if a fetch is still in flight. On that form's submit the pending
   debounce is dropped and the fields are synced once more, so the POST holds
   exactly what is in the box. */
(function () {
  'use strict';

  if (customElements.get('rs-gift-panel')) return;

  var MSG_PREFIX = 'GIFT MESSAGE: ';
  var WRAP_LINE = 'GIFT WRAP: Yes'; // legacy, read-only: recognised, never written
  var LEGACY_ATTR_KEY = 'Gift wrap'; // legacy, delete-only
  // Short on purpose: an express-checkout click reads the server cart, so a
  // long debounce widens the window in which fresh text hasn't landed yet.
  // Leaving the field (blur) still saves at once.
  var DEBOUNCE_MS = 200;

  function updateUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/update.js';
  }

  function composeNote(message) {
    var m = (message || '').replace(/\r\n?/g, '\n').trim();
    return m ? MSG_PREFIX + m : '';
  }

  function normalise(note) {
    return (note || '').replace(/\r\n?/g, '\n');
  }

  function isOwned(note) {
    var head = (note || '').slice(0, MSG_PREFIX.length);
    return head === MSG_PREFIX || head === WRAP_LINE;
  }

  // Same parse as snippets/rs-gift-panel.liquid: drop the first prefix and any
  // legacy wrap line, then trim.
  function messageFrom(note) {
    return normalise(note).replace(MSG_PREFIX, '').split(WRAP_LINE).join('').trim();
  }

  class RsGiftPanel extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this.message = this.querySelector('[data-rs-gift-message]');
      this.noteField = this.querySelector('[data-rs-gift-note]');
      this.count = this.querySelector('[data-rs-gift-count]');
      this.status = this.querySelector('[data-rs-gift-status]');
      if (!this.message || !this.noteField) return;
      this._wired = true;

      this.max = parseInt(this.message.getAttribute('maxlength'), 10) || 0;
      // What the server holds right now. A save is skipped when nothing differs.
      // legacyWrap: the server cart still carries the old "Gift wrap" attribute.
      this.saved = { note: normalise(this.noteField.value), legacyWrap: this.dataset.legacyWrap === 'true' };
      this.owned = this.dataset.owned === 'true';
      this.timer = null;
      this.queue = Promise.resolve();

      this.onInput = this.onInput.bind(this);
      this.onCommit = this.onCommit.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.message.addEventListener('input', this.onInput);
      this.message.addEventListener('change', this.onCommit);
      this.message.addEventListener('blur', this.onCommit);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      // The form named by the hidden note input's form attribute (form#cart on
      // /cart, form#rs-drawer-checkout in the drawer).
      this.form = this.noteField.form;
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      // Bare identifiers, not window.*: PUB_SUB_EVENTS is a top-level const in
      // constants.js, which never becomes a window property. Same guard as
      // rs-paypal-message.js / rs-pdp-set-badge.js.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Cart already empty on load (e.g. emptied from the drawer on another page)
      // but still carrying our gift state: clear it now.
      if (this.dataset.itemCount === '0') { this.clearIfSet(); return; }

      // Legacy gift-wrap state from before 2026-10-02: drop the note line and
      // the attribute now, so neither rides the order. The message is kept.
      if ((this.owned && this.saved.note.indexOf(WRAP_LINE) !== -1) || this.saved.legacyWrap) {
        this.sync();
        this.save(false);
      }
    }

    disconnectedCallback() {
      if (!this._wired) return;
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.unsubscribe) this.unsubscribe();
    }

    // Until the customer types, a note we don't own (one that doesn't start
    // with a gift marker) is carried through unchanged.
    desired() {
      return {
        note: this.owned ? composeNote(this.message.value) : this.saved.note,
        legacyWrap: false
      };
    }

    same(d) {
      return d.note === this.saved.note && d.legacyWrap === this.saved.legacyWrap;
    }

    // Mirror the current state into the form="cart" fallback fields + counter.
    sync() {
      var d = this.desired();
      this.noteField.value = d.note;
      if (this.count) this.count.textContent = this.message.value.length + '/' + this.max;
      this.syncStatus();
    }

    // Closed-card status line (Brand #30). Text comes from the Gifting
    // settings via data-status-* on this element; set with textContent only.
    // Reflects the fields as they stand now, so it is current the moment the
    // customer collapses the card. CSS hides it while the card is open.
    syncStatus() {
      if (!this.status) return;
      var hasMsg = this.message.value.trim() !== '';
      var text = hasMsg ? this.dataset.statusMessage : '';
      this.status.textContent = text || '';
      this.status.hidden = !text;
    }

    onInput() {
      this.owned = true;
      this.sync();
      clearTimeout(this.timer);
      this.timer = setTimeout(this.save.bind(this, false), DEBOUNCE_MS);
    }

    // textarea change, textarea blur. A blur with no edit
    // changes nothing (save() skips when desired === saved).
    onCommit(evt) {
      if (evt && evt.type !== 'blur') this.owned = true;
      this.sync();
      clearTimeout(this.timer);
      this.save(false);
    }

    onPageHide() {
      clearTimeout(this.timer);
      var d = this.desired();
      if (this.same(d)) return;
      this.post(d, true);
    }

    // Checkout POST: the hidden note input travels with the form, so make sure
    // it holds the box's text now, not a debounce behind.
    onSubmit() {
      clearTimeout(this.timer);
      this.sync();
    }

    // Back/forward-cache restore: the page may show text that was changed on
    // another page since. Read the server note again.
    onPageShow(evt) {
      if (!evt || !evt.persisted) return;
      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) { if (cart) self.reseed(cart.note); })
        .catch(function () {});
    }

    // Re-read cart.note into the field. Skipped while the shopper is editing:
    // the box has focus, or holds text not yet saved.
    reseed(note) {
      if (!this._wired || typeof note === 'undefined') return;
      if (document.activeElement === this.message) return;
      if (!this.same(this.desired())) return;
      note = normalise(note);
      if (note === this.saved.note) return;
      this.saved.note = note;
      this.owned = isOwned(note);
      this.message.value = this.owned ? messageFrom(note) : '';
      this.sync();
    }

    onCartUpdate(evt) {
      var cart = evt && evt.cartData;
      if (!cart || typeof cart.item_count !== 'number') return;
      if (cart.item_count !== 0) return;
      // The payload holds the server note: trust it over the page-load seed,
      // so a note written after this panel rendered is still cleared.
      if (typeof cart.note !== 'undefined') {
        this.saved.note = normalise(cart.note);
        this.owned = this.owned || isOwned(this.saved.note);
      }
      this.clearIfSet();
    }

    clearIfSet() {
      var hasNote = this.owned && this.saved.note !== '';
      if (!hasNote && !this.saved.legacyWrap && !this.message.value) return;
      this.message.value = '';
      this.sync();
      this.save(false);
    }

    save(keepalive) {
      var self = this;
      this.queue = this.queue.then(function () {
        var d = self.desired();
        if (self.same(d)) return null;
        return self.post(d, keepalive);
      });
      return this.queue;
    }

    post(d, keepalive) {
      var self = this;
      var body = { note: d.note };
      // Shopify standard event (Digital #97, assets/rs-cart-events.js):
      // cart:note-update. 'dialog' in the rs-drawer, 'cart' on /cart.
      var ce = window.rsCartEvents;
      var noteEvent = ce ? ce.noteUpdate(this, this.closest('#rs-drawer') ? 'dialog' : 'cart', d.note) : null;
      if (this.saved.legacyWrap) {
        // Delete-only: the one legacy key, set to "". Never send any other key.
        body.attributes = {};
        body.attributes[LEGACY_ATTR_KEY] = '';
      }
      return fetch(updateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: !!keepalive,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          // /cart/update.js answers with the whole cart: no extra read.
          if (ce) ce.mutated(cart);
          if (noteEvent) noteEvent.resolve(cart);
          self.saved = {
            note: normalise(cart.note),
            legacyWrap: !!(cart.attributes && cart.attributes[LEGACY_ATTR_KEY])
          };
        })
        .catch(function (err) {
          if (noteEvent) noteEvent.reject(err);
          if (ce) ce.cartError(self, err && err.message, 'SERVICE_UNAVAILABLE');
          // The form="cart" fields still carry the value to a standard checkout.
          console.warn('rs-gift: save failed', err);
        });
    }
  }

  customElements.define('rs-gift-panel', RsGiftPanel);
})();
